import React from 'react'
import { Button as MuiButton } from '@mui/material'
import { littleRed, littleBlue, littleYellow, littleGreen } from './DefineColors'

/**
 * CommonButton - A reusable button component
 * 
 * @param {string} label - The text to display on the button
 * @param {string} backgroundColor - Background color (can be hex, rgb, or predefined color name)
 * @param {string} size - Button size: 'small', 'medium', or 'large' (default: 'medium')
 * @param {string} variant - Button variant: 'contained', 'outlined', or 'text' (default: 'contained')
 * @param {string} textColor - Text color (default: 'white' for contained, 'inherit' for others)
 * @param {function} onClick - Click handler function
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} fullWidth - Whether button should take full width
 * @param {object} sx - Additional MUI sx styles
 * @param {object} ...otherProps - Any other props to pass to MUI Button
 */
const Button = ({
  label = 'Button',
  backgroundColor,
  size = 'medium',
  variant = 'contained',
  textColor,
  onClick,
  disabled = false,
  fullWidth = false,
  sx = {},
  ...otherProps
}) => {
  // Map predefined color names to actual color values
  const colorMap = {
    red: littleRed,
    blue: littleBlue,
    yellow: littleYellow,
    green: littleGreen,
    primary: littleGreen,
    secondary: littleBlue,
    danger: littleRed,
    warning: littleYellow,
  }

  // Get the actual background color
  const bgColor = backgroundColor 
    ? (colorMap[backgroundColor.toLowerCase()] || backgroundColor)
    : (variant === 'contained' ? littleGreen : 'transparent')

  // Determine text color
  const getTextColor = () => {
    if (textColor) return textColor
    if (variant === 'contained') {
      // Use white text for dark backgrounds, black for light backgrounds
      if (backgroundColor === 'yellow' || backgroundColor === littleYellow) {
        return 'black'
      }
      return 'white'
    }
    return 'inherit'
  }

  // Size-based padding - matching document table styling for small size
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { padding: '5px 12px', fontSize: '0.875rem', minHeight: '28px' }
      case 'large':
        return { padding: '10px 22px', fontSize: '1rem', minHeight: '42px' }
      default: // medium
        return { padding: '6px 16px', fontSize: '0.875rem', minHeight: '36px' }
    }
  }

  // Helper function to add opacity to hex color
  const addOpacity = (color, opacity) => {
    if (color.startsWith('#')) {
      const hex = color.slice(1)
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }
    return color
  }

  const buttonStyles = {
    backgroundColor: variant === 'contained' ? bgColor : 'transparent',
    color: getTextColor(),
    border: variant === 'outlined' ? `1px solid ${bgColor}` : 'none',
    borderRadius: '4px',
    fontWeight: 500,
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: variant === 'contained' 
        ? bgColor 
        : addOpacity(bgColor, 0.1), // 10% opacity for outlined/text variants
      opacity: variant === 'contained' ? 0.9 : 1,
      border: variant === 'outlined' ? `1px solid ${bgColor}` : 'none',
    },
    '&:disabled': {
      backgroundColor: variant === 'contained' ? '#e0e0e0' : 'transparent',
      color: '#9e9e9e',
      border: variant === 'outlined' ? '1px solid #e0e0e0' : 'none',
    },
    ...getSizeStyles(),
    ...sx,
  }

  // Support both label prop and children
  const buttonContent = otherProps.children !== undefined ? otherProps.children : label
  
  return (
    <MuiButton
      variant={variant}
      size={size}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={buttonStyles}
      {...otherProps}
    >
      {buttonContent}
    </MuiButton>
  )
}

export default Button